import { atom, selector, DefaultValue, selectorFamily } from 'recoil';
import type { RecoilState } from 'recoil';
import {
  broadcastChannelSynchronizerEffect,
  localForagePersisterEffect,
  localStoragePersisterEffect,
} from 'utils/recoilEffects';
import type { HangingProtocol } from './useHangingProtocol';
import type { StepConfiguration } from './steppedHPTypes';
import { calculateSubStepTotal } from './hpStepController';
import omit from 'ramda/es/omit';
import { env } from 'config/env';
import { StepBarPlacement } from 'generated/graphql';

// Defined locally to avoid depending on Steps/Common.tsx (a UI file) from this pure-state module.
// Must stay in sync with STEP_BAR_DEFAULT_HEIGHT in Steps/Common.tsx.
const STEP_BAR_DEFAULT_HEIGHT = 48;

// Re-export the generated StepBarPlacement enum so the placement atom and the Steps components
// share a single enum (both resolve to generated/graphql) rather than two nominal copies.
export { StepBarPlacement };

export const previousHangingProtocolState: RecoilState<Nullable<HangingProtocol>> = atom<
  Nullable<HangingProtocol>
>({
  key: 'previousHangingProtocolState',
  default: null,
  effects: [
    broadcastChannelSynchronizerEffect(),
    localForagePersisterEffect({
      version: 1,
    }),
  ],
});

export const previewHangingProtocolState: RecoilState<{
  [key: string]: Nullable<HangingProtocol>;
}> = atom({
  key: 'previewHangingProtocolState',
  default: {},
  effects: [
    broadcastChannelSynchronizerEffect(),
    localForagePersisterEffect({
      version: 1,
      __LEGACY_MIGRATION_DO_NOT_USE__disabled: env.NODE_ENV === 'test',
    }),
  ],
});

export const previewHangingProtocolSelector: (
  arg1?: Nullable<string>
) => RecoilState<Nullable<HangingProtocol>> = selectorFamily<
  Nullable<HangingProtocol>,
  Nullable<string>
>({
  key: 'previewHangingProtocolSelector',
  get:
    (caseSmid?: string | null) =>
    ({ get }) => {
      const state = get(previewHangingProtocolState);
      return state != null && caseSmid != null ? state[caseSmid] : null;
    },
  set:
    (caseSmid?: string | null) =>
    ({ set, reset }, hangingProtocol: Nullable<DefaultValue | HangingProtocol>) => {
      if (caseSmid == null) return;

      if (hangingProtocol instanceof DefaultValue && caseSmid != null) {
        set(previewHangingProtocolState, (state) => omit([caseSmid], state));
        return;
      }

      // @ts-expect-error [EN-7967] - TS2345 - Argument of type '(state: { [key: string]: { readonly __typename?: "HangingProtocol"; readonly smid: string; readonly name: string; readonly numberOfPriors: number; readonly created: Date; readonly updated: Date; readonly studyDescriptions: readonly string[]; readonly studyCriteria?: JSON; readonly data: { ...; }; readonly viewportsC...' is not assignable to parameter of type 'DefaultValue | { [key: string]: { readonly __typename?: "HangingProtocol"; readonly smid: string; readonly name: string; readonly numberOfPriors: number; readonly created: Date; ... 4 more ...; readonly viewportsConfigurations?: readonly { ...; }[]; }; } | ((prevValue: { ...; }) => DefaultValue | { ...; })'.
      set(previewHangingProtocolState, (state) => ({
        ...state,
        [caseSmid]: hangingProtocol,
      }));
    },
});

export const isPreviewingHangingProtocolState: RecoilState<boolean> = atom({
  key: 'isPreviewingHangingProtocolState',
  default: false,
  effects: [broadcastChannelSynchronizerEffect()],
});

export const hangingProtocolStepIndex: RecoilState<number> = atom({
  key: 'hpStepIndex',
  default: 0,
  effects: [broadcastChannelSynchronizerEffect()],
});

/**
 * Current sub-step index within a step (for prior scrolling).
 * Reset to 0 by callers when navigating to a new step (see `navigateToStep` in
 * `useHPStepOperations` and HP initialization in `useHangingProtocol`). There is no
 * automatic selector/effect wiring on the atom itself.
 */
export const hangingProtocolSubStepIndex: RecoilState<number> = atom({
  key: 'hpSubStepIndex',
  default: 0,
  effects: [broadcastChannelSynchronizerEffect()],
});

/**
 * Versioned wrapper for stepped HP configurations.
 * The version counter prevents stale BroadcastChannel echoes from overwriting
 * authoritative local updates (e.g., a clinical step reorder on viewer/0 being
 * overwritten by viewer/1's stale refetch data echoing back).
 */
export type VersionedStepConfigurations = {
  steps: StepConfiguration[] | null;
  /** HP display name, kept here so activeHangingProtocolName can be a pure selector. */
  name: string | null;
  version: number;
};

/**
 * BroadcastChannel merge function for steppedHPConfigurations.
 * Only accepts incoming values with a strictly higher version.
 * Exported for testing.
 */
export const steppedHPMergeFn = (
  oldValue: Nullable<VersionedStepConfigurations>,
  newValue: VersionedStepConfigurations
): VersionedStepConfigurations => {
  const oldVersion = oldValue?.version ?? 0;
  return newValue.version > oldVersion ? newValue : (oldValue ?? newValue);
};

const DEFAULT_VERSIONED_STEPS: VersionedStepConfigurations = {
  steps: null,
  name: null,
  version: 0,
};

/**
 * Step configurations array from a stepped hanging protocol.
 * null when using legacy (flat) HP format.
 *
 * Wrapped in { steps, version } to prevent BroadcastChannel race conditions.
 */
export const steppedHPConfigurations: RecoilState<VersionedStepConfigurations> =
  atom<VersionedStepConfigurations>({
    key: 'steppedHPConfigurations',
    default: DEFAULT_VERSIONED_STEPS,
    effects: [broadcastChannelSynchronizerEffect({ mergeFn: steppedHPMergeFn })],
  });

/** Read just the steps array from the versioned atom value. */
export const getStepsFromVersioned = (
  versioned: VersionedStepConfigurations
): StepConfiguration[] | null => versioned.steps;

/** Derived from steppedHPConfigurations — resets automatically when the HP changes. */
export const activeHangingProtocolName = selector<string | null>({
  key: 'activeHangingProtocolName',
  get: ({ get }) => get(steppedHPConfigurations).name,
});

/**
 * Total number of sub-steps for the current step.
 * Derived from steppedHPConfigurations and hangingProtocolStepIndex so it stays
 * in sync without imperative setters.
 * 0 means no sub-steps (normal step without prior scrolling).
 */
export const hangingProtocolSubStepTotal = selector<number>({
  key: 'hpSubStepTotal',
  get: ({ get }) => {
    const stepIndex = get(hangingProtocolStepIndex);
    const step = getStepsFromVersioned(get(steppedHPConfigurations))?.[stepIndex] ?? null;
    return calculateSubStepTotal(step);
  },
});

/**
 * Create a versioned steps value with an incremented version counter.
 * Use this for all writes to steppedHPConfigurations to ensure the version
 * counter advances, making the write authoritative over stale echoes.
 */
export const makeVersionedSteps = (
  steps: StepConfiguration[] | null,
  prev: VersionedStepConfigurations,
  name?: string | null
): VersionedStepConfigurations => ({
  steps,
  name: name !== undefined ? name : prev.name,
  version: prev.version + 1,
});

export type StepsUpdater =
  | StepConfiguration[]
  | null
  | ((prev: StepConfiguration[] | null) => StepConfiguration[] | null);

/**
 * Wraps a versioned atom setter so callers can work with plain StepConfiguration[] updaters.
 * Handles unwrapping prev.steps and re-wrapping with makeVersionedSteps.
 */
export function applyVersionedStepsUpdate(
  setVersioned: (
    updater: (prev: VersionedStepConfigurations) => VersionedStepConfigurations
  ) => void,
  updater: StepsUpdater
): void {
  setVersioned((prev) => {
    const newSteps = typeof updater === 'function' ? updater(prev.steps) : updater;
    return makeVersionedSteps(newSteps, prev);
  });
}

/**
 * Current step configuration derived from steppedHPConfigurations and hangingProtocolStepIndex.
 * Read-only selector — no imperative sync needed since source atoms already have BroadcastChannel.
 */
export const currentStepConfiguration = selector<StepConfiguration | null>({
  key: 'currentStepConfiguration',
  get: ({ get }) => {
    const versioned = get(steppedHPConfigurations);
    const stepIndex = get(hangingProtocolStepIndex);
    return getStepsFromVersioned(versioned)?.[stepIndex] ?? null;
  },
});

/**
 * Step configurations owned by the HP form during editing.
 * Isolated from the viewer's steppedHPConfigurations to prevent
 * race conditions when the matched HP reference changes.
 * null when the form is closed or steps are not enabled.
 */
export const hpFormStepConfigurations: RecoilState<StepConfiguration[] | null> = atom<
  StepConfiguration[] | null
>({
  key: 'hpFormStepConfigurations',
  default: null,
  effects: [],
});

/**
 * User preference to skip the "Discard changes?" confirmation modal.
 * Does not persist through page refreshes.
 */
export const skipDiscardConfirmationState: RecoilState<boolean> = atom({
  key: 'skipDiscardConfirmationState',
  default: false,
});

/**
 * Stores the initial form state when entering HP edit/create mode.
 * Used to detect if there are unsaved changes.
 */
export const initialHPFormStateSnapshot: RecoilState<unknown | null> = atom<unknown | null>({
  key: 'initialHPFormStateSnapshot',
  default: null,
});

/**
 * Baseline step configurations when entering HP edit/create mode.
 * Used to detect if there are unsaved changes to steps.
 */
export const hpFormStepsBaseline: RecoilState<StepConfiguration[] | null> = atom<
  StepConfiguration[] | null
>({
  key: 'initialStepConfigurationsSnapshot',
  default: null,
});

/**
 * Baseline of steppedHPConfigurations at HP load time (or last clinical save).
 * Used to detect per-step dirty state in clinical mode.
 */
export const clinicalStepsBaseline: RecoilState<StepConfiguration[] | null> = atom<
  StepConfiguration[] | null
>({
  key: 'clinicalStepBaseline',
  default: null,
  effects: [broadcastChannelSynchronizerEffect()],
});

/**
 * Whether a clinical step creation is in flight.
 * Synced across windows so both viewer/0 and viewer/1 disable the
 * "Add Step" button while the mutation is pending.
 */
export const isCreatingStepState: RecoilState<boolean> = atom({
  key: 'isCreatingStepState',
  default: false,
  effects: [broadcastChannelSynchronizerEffect()],
});

/**
 * User preference for step bar position relative to viewports.
 * Persisted to localStorage and synced across windows.
 */
export const stepBarPlacementState: RecoilState<StepBarPlacement> = atom<StepBarPlacement>({
  key: 'stepBarPlacement',
  default: StepBarPlacement.Bottom,
  effects: [broadcastChannelSynchronizerEffect(), localStoragePersisterEffect({ version: 1 })],
});

/**
 * Persisted step bar height in pixels. Synced across viewer windows so that
 * resizing on viewer/0 is reflected on viewer/1. A value of 0 means the bar
 * is collapsed.
 */
export const stepBarHeightState: RecoilState<number> = atom<number>({
  key: 'stepBarHeight',
  default: STEP_BAR_DEFAULT_HEIGHT,
  effects: [broadcastChannelSynchronizerEffect(), localStoragePersisterEffect({ version: 1 })],
});
